import { React, useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import PSPage from "components/globals/ui/layout/Page"
import { formatDate, useFetchAuthedData, formatTitle, getCategoryString, getTypeString } from "helpers/utils";
import parse from "html-react-parser";
import IsYoursBanner from 'components/globals/IsYoursBanner'
import Single from "components/globals/image/Single";
import Comments from "components/globals/comments/Comments";
import UserBadge from "components/globals/user/badge";
import Likes from "components/globals/likes/Likes";
import {TypeBadge, CategoryBadge} from "components/globals/ui/TypeBadge";
import {ORSMasterListing} from "components/globals/ORSMasterListing"
import ORSGridDetailItem from "components/globals/ui/ORSGridDetailItem";
import ContactBtn from "components/globals/user/ContactBtn";
import Banner from "components/globals/image/Banner";
import {
  PSCollapsableContainer,
  PSCollapsableContainerHeading,
  PSCollapsableContainerContent,
} from "components/globals/ui/PSCollapsableContainer";
import { Button } from "components/globals/ui/input/Button";
import { attendEvent, declineEvent } from "store/authSlice";
import { useDispatch } from "react-redux";
import {ORSSubnav, SubnavItem} from "components/globals/ORSSubnav";


const EventPage = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const [loadingEventStatus, setLoadingEventStatus] = useState(false);
	const [isAttending, setIsAttending] = useState(false);
	const [refreshAttendees, setRefreshAttendees] = useState(0);

	// double entry, entry.entry & entry.user
	const { data: entry, loading, error } = useFetchAuthedData(
		`event/${id}`,
		[id],
	);

	const { data: isAttendingEvent } = useFetchAuthedData(
		`eventAttending/${id}`,
		[id],
	);

	useEffect(() => {
		if(isAttendingEvent?.entry) {
			setIsAttending(true);
		}
	}, [isAttendingEvent]);

	const requestEventAttendance = async () => {
		try {
				setLoadingEventStatus(true);
				await dispatch(attendEvent({ data: {id: id} })).unwrap();
				setIsAttending(true);
				// Trigger a refresh of the attendees list
				setRefreshAttendees(prev => prev + 1);
				window.location.reload();

		} catch (err) {
				// Handle error
		} finally {
				setLoadingEventStatus(false);
		}
	}

	const requestEventDecline = async () => {
		try {
				setLoadingEventStatus(true);
				await dispatch(declineEvent({ data: {id: id} })).unwrap();
				setIsAttending(false);
				// Trigger a refresh of the attendees list
				setRefreshAttendees(prev => prev + 1);
				window.location.reload();

		} finally {
				setLoadingEventStatus(false);
		}
	}

	const sections = [
		{
			params: {
				elementId: "userFeed", 
				headingTitle: "Other Events",
				headingButtonUrl: "",
				headingButtonText: "",
				apiPath: `event`,
				omit: entry?.entry?.internal_id,
				limit: "12",
				type: "event", 
				pagination: true,
			},
			displayOptions: {
				layout: "card",
				yoursBadge: false,
				typeBadge: true,
				categoryBadge: false,
				userBadge: false,
				carBadge: false,
				carDetails: false,
				dateDetails: false,
				rowColumns: true,
				mobileCarousel: false,
				likes: true,
			}
		}
	]

	const attendees = {
		params: {
			elementId: "attendees", 
			headingTitle: null,
			shortTitle: null,
			headingButtonUrl: "",
			headingButtonText: "",
			apiPath: `event/attending/${id}`,
			omit: "none",
			limit: "100",
			type: "users", 
			pagination: false,
			refreshTrigger: refreshAttendees,
		},
		displayOptions: {
			layout: "row",
			yoursBadge: false,
			userBadge: false,
			typeBadge: false,
			categoryBadge: false,
			carBadge: false,
			carDetails: false,
			dateDetails: false,
			rowColumns: false,
			mobileCarousel: false,
			likes: false,
			shortRow: true,
		}
	}

	// Loading & error states
	if (loading) return <div>Loading...</div>;
	if (error) return <div>Error: {error.message}</div>;

	const hasBanners = Array.isArray(entry.entry.banners) && entry.entry.banners.length > 0;

	return (
		<>
			<ORSSubnav>
				<SubnavItem to="/society" label="Society" />
				<SubnavItem to="/society/members" label="Members" />
				<SubnavItem to="/society/events" label="Events" />
				<SubnavItem to="/society/rallys" label="Drives & Rallys" />
			</ORSSubnav>

			<IsYoursBanner label="Your Event" entry={entry.entry} />

			<PSPage>

				{hasBanners && (
					<Banner image={entry.entry.banners[0]} />
				)}

				<div className="flex items-start flex-wrap md:flex-nowrap gap-sm mt-sm">

					<div className="md:sticky md:top-[100px] w-full md:w-1/2 rounded-default overflow-hidden">
						<img
              className='block w-full'
              src={`//ps-images.imgix.net/${entry.entry.gallery[0].filename}?q=85&width=600`}
              alt="baseImage"
            />
					</div>

					<div className="w-full md:w-1/2">
						
						<div className="my-1 flex flex-wrap items-center inline-block gap-2">
							<TypeBadge entryType={entry.entry.entry_type} type={entry.entry.type} category={entry.entry.category} />
							<CategoryBadge entryType={entry.entry.entry_type} type={entry.entry.type} category={entry.entry.category} />
						</div>
						
						<h1 className="text-3xl ">{ formatTitle(entry.entry.title) }</h1>
	
						<div>
							<Likes 
								entry_type={entry.entry.entry_type} 
								entry_id={entry.entry.internal_id}
							/>
						</div>

						
						{entry?.entry?.body && (
							<div className="mt-xs mb-md ors-rich-text">{parse(entry?.entry?.body)}</div>
						)}

						{entry.entry.user_id && (
							<div className="p-2 bg-secondary mb-3 w-full rounded-lg">
								<p className=" text-[12px] mb-1">EVENT ORGANIZER:</p>
								<UserBadge userId={entry.entry.user_id} />
								
								<div className="mt-3">
									<ContactBtn userId={entry.entry.user_id} labelSuffix=" about this event" />
								</div>

							</div>
						)}

						<div className="w-full grid grid-cols-2 gap-2">
							{entry.entry.event_date && entry.entry.event_type === "single" && (
								<ORSGridDetailItem title="Event Date" value={formatDate(entry.entry.event_date)} />
							)}
							{entry.entry.recurring_frequency && entry.entry.event_type === "recurring" && (
								<ORSGridDetailItem title="Event Date" value={entry.entry.recurring_frequency} />
							)}
							{entry.entry.location && (
								<ORSGridDetailItem title="Location" value={entry.entry.location} />
							)}
							{entry.entry.event_time && (
								<ORSGridDetailItem title="Time" value={entry.entry.event_time} />
							)}
							{entry.entry.category && (
								<ORSGridDetailItem title="Category" value={getCategoryString(entry.entry.category)} />
							)}
							{entry.entry.type && (
								<ORSGridDetailItem title="Type" value={getTypeString(entry.entry.type)} />
							)}
						</div>

						
						<div className="mt-12">
							<PSCollapsableContainer startShown={true}>
								<PSCollapsableContainerHeading>
									<h3 className="text-xl ">Attendees</h3>
								</PSCollapsableContainerHeading>

								<PSCollapsableContainerContent>
								<Button 
										disabled={loadingEventStatus} 
										variant={isAttending ? "destructive" : "outline"}
										onClick={isAttending ? requestEventDecline : requestEventAttendance}
									>
										{isAttending ? "I would like to decline attendance" : "I would like to attend"}
									</Button>

									<div className="py-xs">
										<ORSMasterListing 
											key={`attendees-list-${refreshAttendees}`} 
											params={attendees.params} 
											displayOptions={attendees.displayOptions}
										/>
									</div>
								</PSCollapsableContainerContent>
							</PSCollapsableContainer>
						</div>
						

						<Comments 
							entry_type={entry.entry.entry_type} 
							entry_id={entry.entry.internal_id}
						/>

					</div>

				</div>

				{entry.entry.location_url && entry.entry.location_url.startsWith('https://') && (
					<iframe 
						className="rounded-lg overflow-hidden my-md"
						title="iframe"
						src={entry.entry.location_url} 
						width="100%" 
						height="450" 
						loading="lazy" 
						referrerPolicy="no-referrer-when-downgrade">
					</iframe>
				)}

				{sections.map(({params, displayOptions}) => (
					<ORSMasterListing key={params.elementId} params={params} displayOptions={displayOptions} />
				))}


			</PSPage>
		</>
		
	);
};

export default EventPage;
